import React, { useContext, useEffect, useState } from "react";
import Breadcrumb from "../../../CommonComponents/BreadCrumb/BreadCrumb";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import Button from "../../../Elements/Button";
import { networkService } from "../../../config/axios";
import DeleteOrgModal from "../../../Modals/Admin/DeleteOrgModal";
import AddModelRoutingModal from "../../../Modals/Admin/AddModelRoutingModal";
import UserInfoContext from "../../../../utils/userInfoContext";
import ModelVersionSelect from "./ModelVersionSelect";
import "../admin.css";

const SCOPE_LABELS = {
    global: "Global fallback",
    task: "Task default",
    client_task: "Client override",
};

const AdminModelRoutingPage = () => {
    const { setShowNotifications } = useContext(UserInfoContext)
    const [configs, setConfigs] = useState([])
    const [modelFamilies, setModelFamilies] = useState({})
    const [taskTypes, setTaskTypes] = useState([])
    const [restrictedTaskTypes, setRestrictedTaskTypes] = useState([])
    const [companies, setCompanies] = useState([])
    const [currentUser, setCurrentUser] = useState(null)
    const [showAddModal, setShowAddModal] = useState(false)
    const [deleteId, setDeleteId] = useState(null)
    const [companyApiKeyPresent, setCompanyApiKeyPresent] = useState(false)
    const [companyApiKeyInput, setCompanyApiKeyInput] = useState("")
    const [showCompanyApiKey, setShowCompanyApiKey] = useState(false)
    const [savingCompanyApiKey, setSavingCompanyApiKey] = useState(false)

    const isSuperAdmin = !!currentUser?.super_admin;

    const refreshConfigs = async () => {
        const response = await networkService("admin/model_routing_configs.json", "GET");
        setConfigs(response.data?.model_routing_configs || []);
        setModelFamilies(response.data?.model_families || {});
        setTaskTypes(response.data?.task_types || []);
        setRestrictedTaskTypes(response.data?.restricted_task_types || []);
        setCurrentUser(response.data?.current_user || null);
        setCompanyApiKeyPresent(!!response.data?.company_claude_api_key_present);
    };

    const handleSaveCompanyApiKey = async () => {
        if (!companyApiKeyInput.trim()) return;
        setSavingCompanyApiKey(true);
        try {
            const response = await networkService("admin/model_routing_configs/company_api_key.json", "PATCH", {
                company: { claude_api_key: companyApiKeyInput },
            });
            if (response.status !== 200) throw new Error();
            setCompanyApiKeyPresent(!!response.data?.company_claude_api_key_present);
            setCompanyApiKeyInput("");
            setShowNotifications({ showToast: true, toastType: "success", toastMessage: "Company API key saved" });
        } catch (error) {
            setShowNotifications({ showToast: true, toastType: "danger", toastMessage: "Something went wrong" });
        } finally {
            setSavingCompanyApiKey(false);
        }
    };

    useEffect(() => {
        refreshConfigs();
    }, []);

    useEffect(() => {
        if (!isSuperAdmin) return;
        networkService("admin/orgs.json", "GET").then(response => {
            setCompanies(response.data || []);
        });
    }, [isSuperAdmin]);

    const handleModelChange = async (id, model_id) => {
        let previous;
        setConfigs(prev => {
            previous = prev;
            return prev.map(c => c.id === id ? { ...c, model_id } : c);
        });

        try {
            const response = await networkService(`admin/model_routing_configs/${id}.json`, "PATCH", {
                model_routing_config: { model_id }
            });
            if (response.status !== 200) throw new Error();
            setShowNotifications({ showToast: true, toastType: "success", toastMessage: "Model updated" });
        } catch (error) {
            setConfigs(previous);
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: error?.response?.data?.errors?.join(", ") || "Something went wrong",
            });
        }
    };

    const handleActiveChange = async (id, active) => {
        let previous;
        setConfigs(prev => {
            previous = prev;
            return prev.map(c => c.id === id ? { ...c, active } : c);
        });

        try {
            const response = await networkService(`admin/model_routing_configs/${id}.json`, "PATCH", {
                model_routing_config: { active }
            });
            if (response.status !== 200) throw new Error();
        } catch (error) {
            setConfigs(previous);
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage: error?.response?.data?.errors?.join(", ") || "Something went wrong",
            });
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await networkService(`admin/model_routing_configs/${id}.json`, "DELETE");
            if (response.status === 204) {
                setDeleteId(null);
                await refreshConfigs();
                setShowNotifications({ showToast: true, toastType: "success", toastMessage: "Config deleted" });
            }
        } catch (error) {
            setDeleteId(null);
            setShowNotifications({ showToast: true, toastType: "danger", toastMessage: "Something went wrong" });
        }
    };

    const canEditRow = (original) =>
        isSuperAdmin ||
        (original?.scope_type === "client_task" && !restrictedTaskTypes.includes(original?.task_type));

    const columns = React.useMemo(() => {
        const cols = [
            {
                Header: "Scope",
                accessor: "scope_type",
                Cell: ({ value }) => SCOPE_LABELS[value] || value,
            },
            {
                Header: "Task",
                accessor: "task_type",
                Cell: ({ row: { original } }) => (original?.scope_type === "global" ? "—" : original?.task_type),
            },
        ];

        if (isSuperAdmin) {
            cols.push({
                Header: "Client",
                accessor: "company_name",
                Cell: ({ value }) => value || "—",
            });
        }

        cols.push(
            {
                Header: "Model",
                accessor: "model_id",
                Cell: ({ row: { original } }) => (
                    <ModelVersionSelect
                        modelFamilies={modelFamilies}
                        value={original?.model_id}
                        disabled={!canEditRow(original)}
                        onChange={(model_id) => handleModelChange(original.id, model_id)}
                    />
                ),
            },
            {
                Header: "Active",
                accessor: "active",
                Cell: ({ row: { original } }) => (
                    <input
                        type="checkbox"
                        checked={!!original?.active}
                        disabled={!canEditRow(original)}
                        onChange={(e) => handleActiveChange(original.id, e.target.checked)}
                    />
                ),
            },
            {
                Header: "Actions",
                accessor: "action",
                Cell: ({ row: { original } }) => (
                    canEditRow(original) ? (
                        <img
                            src="/icons/delete.svg"
                            alt=""
                            className="cancel cursor-pointer admin-model-routing-action-icon"
                            onClick={() => setDeleteId(original.id)}
                            role="button"
                        />
                    ) : null
                ),
            },
        );

        return cols;
    }, [modelFamilies, isSuperAdmin]);

    return (
        <>
            <DeleteOrgModal
                title="Delete Model Routing Config"
                openModal={deleteId !== null}
                closeModal={() => setDeleteId(null)}
                name="delete this model routing config"
                handleDelete={() => handleDelete(deleteId)}
            />

            <AddModelRoutingModal
                openModal={showAddModal}
                closeModal={() => setShowAddModal(false)}
                taskTypes={isSuperAdmin ? taskTypes : taskTypes.filter(t => !restrictedTaskTypes.includes(t))}
                modelFamilies={modelFamilies}
                companies={companies}
                isSuperAdmin={isSuperAdmin}
                currentCompanyName={currentUser?.company_name}
                onCreated={refreshConfigs}
            />

            <div className="admin-module">
                <div className="admin-model-routing-page admin-content">
                    <Breadcrumb string="Admin / Model Routing" />

                    <div className="container admin-model-routing-container">
                        {currentUser?.company_id && (
                            <div className="admin-model-routing-api-key mb-4">
                                <h3 className="admin-model-routing-api-key-title">Company Claude API key</h3>
                                <p className="admin-model-routing-api-key-hint">
                                    Used for AI-powered features across this company, instead of the platform default key.
                                    {companyApiKeyPresent ? " A key is currently saved." : " No key saved yet."}
                                </p>
                                <div className="admin-form-group admin-model-routing-api-key-group">
                                    <label htmlFor="company_claude_api_key" className="admin-form-label">Claude API Key</label>
                                    <div className="d-flex align-items-start gap-2">
                                        <div className="admin-input-append-wrapper admin-model-routing-api-key-wrapper">
                                            <input
                                                id="company_claude_api_key"
                                                name="company_claude_api_key"
                                                type={showCompanyApiKey ? "text" : "password"}
                                                placeholder={companyApiKeyPresent ? "Key saved — enter a new key to replace it" : "sk-ant-..."}
                                                value={companyApiKeyInput}
                                                onChange={(e) => setCompanyApiKeyInput(e.target.value)}
                                                autoComplete="off"
                                                className="admin-form-input"
                                            />
                                            <button
                                                type="button"
                                                className="admin-input-append admin-input-append--button"
                                                onClick={() => setShowCompanyApiKey((prev) => !prev)}
                                            >
                                                {showCompanyApiKey ? "Hide" : "Show"}
                                            </button>
                                        </div>
                                        <Button
                                            classList="success"
                                            text={savingCompanyApiKey ? "Saving..." : "Save key"}
                                            type="button"
                                            onClick={handleSaveCompanyApiKey}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="d-flex justify-content-end mb-4 admin-model-routing-header">
                            <Button
                                classList="success"
                                addonClass="admin-model-routing-cta"
                                text="+ Add Override"
                                onClick={() => setShowAddModal(true)}
                                type="button"
                                aria-label="Add model routing override"
                            />
                        </div>

                        <div className="top-parent admin-model-routing-table-wrapper">
                            <CustomTable data={configs} columnData={columns} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default AdminModelRoutingPage;
